import { useNavigate } from "react-router-dom";
import { FolderKanban, Settings, Trash2, UploadCloud } from "lucide-react";
import { AppShell } from "@/components/layout/app-shell";
import { NotificationBell } from "@/components/layout/notification-bell";
import { UserMenu } from "@/components/layout/user-menu";
import type { RailNavItem } from "@/components/layout/sidebar-rail";
import { SearchOverlay } from "@/components/layout/search-overlay";
import {
  SearchOverlayProvider,
  useSearchOverlay,
} from "@/components/layout/search-overlay-context";
import { useAuth, useLogout } from "@/hooks/use-auth";
import { roleDisplayName } from "@/lib/api-types";

export default function AppLayout() {
  // Provider sits above AppShell so both the rail (Search icon) AND deeper
  // pages (project-detail / folder-detail search buttons) share the same
  // overlay state.
  return (
    <SearchOverlayProvider>
      <AppLayoutInner />
      <AppOverlayHost />
    </SearchOverlayProvider>
  );
}

function AppLayoutInner() {
  const { user, role, workspaceName } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();

  const NAV: RailNavItem[] = [
    { kind: "link", to: "/app/projects", label: "Projects", icon: FolderKanban },
    { kind: "link", to: "/app/upload", label: "Upload", icon: UploadCloud },
    { kind: "link", to: "/app/trash", label: "Trash", icon: Trash2 },
    {
      kind: "custom",
      key: "notifications",
      render: () => <NotificationBell to="/app/notifications" />,
    },
    { kind: "link", to: "/app/settings", label: "Settings", icon: Settings },
  ];

  return (
    <AppShell
      brandTo="/app/projects"
      primaryNav={NAV}
      searchPlaceholder="Search projects…"
      projectsBasePath="/app/projects"
      userMenu={
        <UserMenu
          name={user?.name || user?.email || "Account"}
          email={user?.email}
          roleLabel={roleDisplayName(role)}
          workspaceName={workspaceName ?? undefined}
          settingsTo="/app/settings"
          isLoggingOut={logout.isPending}
          onLogout={() =>
            logout.mutate(undefined, { onSuccess: () => navigate("/login", { replace: true }) })
          }
        />
      }
    />
  );
}

function AppOverlayHost() {
  const { open, scope, close } = useSearchOverlay();
  return <SearchOverlay open={open} onClose={close} scope={scope} basePath="/app" />;
}
